import { useAccountAspect } from "@oysterhr/identity/AccountAspectContext"
import { useConfiguredToasts, useReusableWizard } from "@oysterhr/feature-components"
import {
  CountryIsoCode,
  CurrencyEnum,
  PaymentMethodsCustomerBankAccountEnumType,
} from "@oysterhr/schema"
import { useUpdateCustomerBankAccount } from "../../../../hooks/useUpdateCustomerBankAccount"
import { useCreateCustomerBankAccount } from "../../../../hooks/useCreateCustomerBankAccount"
import { AddSepaFormValues } from "../../../types"

export const useFTESepaPaymentMethod = () => {
  const { accountAspect: companyId } = useAccountAspect()
  const { goToNext } = useReusableWizard()
  const { displayErrorToast } = useConfiguredToasts()

  const [updateCustomerBankAccount] = useUpdateCustomerBankAccount()
  const [createCustomerBankAccount, { loading: creatingCustomerBankAccount }] =
    useCreateCustomerBankAccount()

  const updateFTEPaymentMethod = async (
    customerBankAccountId: string,
    values: AddSepaFormValues,
  ) => {
    return await updateCustomerBankAccount({
      variables: {
        customerBankAccountId,
        bankAccountType: null,
        billingAddress: {
          addressLine1: values.addressLine1 ?? "",
          addressLine2: values.addressLine2,
          countryCode: values.countryCode ?? "",
          city: values.city ?? "",
          postalCode: values.postalCode ?? "",
          region: values.region ?? "",
        },
        bankDetails: {
          sepa: {
            accountHolderName: values.name,
            countryCode: values.countryCode as CountryIsoCode,
            currencyCode: CurrencyEnum.Eur,
            email: values.email,
            iban: values.iban,
            swiftBicCode: values.swiftBicCode ?? "",
          },
        },
        userNotSigner: !values.isUserSigner,
      },
      onCompleted: data => {
        if (data.updateCustomerBankAccount.errors.length) {
          displayErrorToast({
            content: data.updateCustomerBankAccount.errors.join(", "),
          })
        } else {
          goToNext()
        }
      },
    })
  }

  const createFTEPaymentMethod = async (values: AddSepaFormValues) => {
    return await createCustomerBankAccount({
      variables: {
        ownerId: companyId,
        paymentMethodType: PaymentMethodsCustomerBankAccountEnumType.Sepa,
        billingAddress: {
          addressLine1: values.addressLine1 ?? "",
          addressLine2: values.addressLine2,
          countryCode: values.countryCode ?? "",
          city: values.city ?? "",
          postalCode: values.postalCode ?? "",
          region: values.region ?? "",
        },
        bankDetails: {
          sepa: {
            accountHolderName: values.name,
            countryCode: values.countryCode as CountryIsoCode,
            currencyCode: CurrencyEnum.Eur,
            email: values.email,
            iban: values.iban,
            swiftBicCode: values.swiftBicCode ?? "",
          },
        },
        userNotSigner: !values.isUserSigner,
        bankAccountType: null,
      },
      onCompleted: data => {
        if (data.createCustomerBankAccount.errors.length) {
          displayErrorToast({
            content: data.createCustomerBankAccount.errors.join(", "),
          })
        } else {
          goToNext()
        }
      },
    })
  }

  const upsertFTEPaymentMethod = async (
    values: AddSepaFormValues,
    customerBankAccountId: string,
  ) => {
    return customerBankAccountId
      ? updateFTEPaymentMethod(customerBankAccountId, values)
      : createFTEPaymentMethod(values)
  }

  return {
    upsertFTEPaymentMethod,
    loading: creatingCustomerBankAccount,
    isReady: true, // FTE doesn't need any external services to be ready
  }
}
